import React, { useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Alert
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Subscriptions as SubscriptionsIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import PaymentMethodsTab from './PaymentMethodsTab';
import SubscriptionManagementTab from './SubscriptionManagementTab';
import UsageDashboard from './UsageDashboard';
import InvoiceHistoryTab from './InvoiceHistoryTab';

/**
 * Billing Tab
 *
 * Main container for all billing-related components
 * Provides tabbed navigation between different billing sections
 */
const BillingTab = ({ organizationId, organizations = [] }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  // Get current organization details
  const currentOrg = organizations.find(org => org.id === organizationId || org.organizationid === organizationId);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Tab panel component
  const TabPanel = ({ children, value, index }) => {
    if (value !== index) {
      return null;
    }

    return (
      <Box
        role="tabpanel"
        id={`billing-tabpanel-${index}`}
        aria-labelledby={`billing-tab-${index}`}
        sx={{ pt: 3 }}
      >
        {children}
      </Box>
    );
  };

  // Check if organization is selected
  if (!organizationId || !currentOrg) {
    return (
      <Box p={3}>
        <Alert severity="info">
          Please select an organization from the menu to manage billing.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Billing & Subscriptions
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage payment methods, subscriptions, and view billing history for{' '}
          <strong>{currentOrg.name}</strong>
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="billing tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 64,
              fontWeight: 500,
              fontSize: '0.95rem',
              '&.Mui-selected': {
                color: theme.palette.waltero.primary.coreBlue,
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.waltero.primary.coreBlue,
              height: 3
            }
          }}
        >
          <Tab
            label="Subscriptions"
            icon={<SubscriptionsIcon />}
            iconPosition="start"
            id="billing-tab-0"
            aria-controls="billing-tabpanel-0"
          />
          <Tab
            label="Payment Methods"
            icon={<CreditCardIcon />}
            iconPosition="start"
            id="billing-tab-1"
            aria-controls="billing-tabpanel-1"
          />
          <Tab
            label="Usage & Analytics"
            icon={<AssessmentIcon />}
            iconPosition="start"
            id="billing-tab-2"
            aria-controls="billing-tabpanel-2"
          />
          <Tab
            label="Invoice History"
            icon={<ReceiptIcon />}
            iconPosition="start"
            id="billing-tab-3"
            aria-controls="billing-tabpanel-3"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <TabPanel value={activeTab} index={0}>
          <SubscriptionManagementTab organizationId={organizationId} />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <PaymentMethodsTab organizationId={organizationId} />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <UsageDashboard organizationId={organizationId} />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <InvoiceHistoryTab organizationId={organizationId} />
        </TabPanel>
      </Box>

      {/* Footer Note */}
      <Box
        sx={{
          mt: 4,
          p: 2,
          backgroundColor: theme.palette.grey[50],
          borderRadius: 1,
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" color="textSecondary">
          All payments are processed securely through Stripe. We never store your card details.
          For billing support, contact{' '}
          <a
            href="mailto:billing@waltero.com"
            style={{ color: theme.palette.waltero.primary.coreBlue }}
          >
            billing@waltero.com
          </a>
        </Typography>
      </Box>
    </Box>
  );
};

export default BillingTab;